import axios from 'axios';
import { toast } from 'sonner';
import { getCsrfToken, refreshCsrfToken } from './csrf';

// Global flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

// Request interceptor: Add CSRF token to all requests
axios.interceptors.request.use(
    (config) => {
        // Get fresh CSRF token for each request
        try {
            const token = getCsrfToken();
            config.headers['X-CSRF-TOKEN'] = token;
            config.headers['X-Requested-With'] = 'XMLHttpRequest';
        } catch {
            // If token not found, request will likely fail - but let it fail naturally
            console.warn('CSRF token not available for axios request');
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    },
);

// Response interceptor: Handle 419 CSRF token mismatch
axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        // Suppress "Request aborted" errors (ECONNABORTED) - these are expected
        // when navigating between pages or when components unmount
        if (error.code === 'ECONNABORTED' || error.message === 'Request aborted') {
            // Return a rejected promise but mark it as handled to prevent console logging
            // Components should still handle these errors, but they won't show in console
            const handledError = new Error('Request was cancelled');
            handledError.name = 'CancelledError';
            // @ts-expect-error - Adding custom property to identify cancelled requests
            handledError.isCancelled = true;
            return Promise.reject(handledError);
        }

        // Handle 419 CSRF token mismatch
        if (error.response?.status === 419) {
            // Prevent multiple simultaneous refresh attempts
            if (isRefreshing && refreshPromise) {
                await refreshPromise;
                // Retry the original request with the new token
                error.config.headers['X-CSRF-TOKEN'] = getCsrfToken();
                return axios.request(error.config);
            }

            isRefreshing = true;
            refreshPromise = refreshCsrfToken();

            try {
                await refreshPromise;
                toast.info('Session refreshed. Retrying...');

                // Retry the original request with the new token
                error.config.headers['X-CSRF-TOKEN'] = getCsrfToken();
                return axios.request(error.config);
            } catch (refreshError) {
                // Refresh failed - user needs to reload
                toast.error('Session expired. Please refresh the page.');
                window.location.reload();
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
                refreshPromise = null;
            }
        }

        // For all other errors, log them normally
        return Promise.reject(error);
    },
);

// Global handler for unhandled promise rejections to suppress ECONNABORTED errors
if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event) => {
        const error = event.reason;
        // Suppress axios aborted request errors from console
        if (
            error?.code === 'ECONNABORTED' ||
            error?.message === 'Request aborted' ||
            (error?.name === 'AxiosError' && error?.code === 'ECONNABORTED') ||
            error?.isCancelled === true
        ) {
            event.preventDefault(); // Prevent the error from being logged to console
        }
    });
}

export default axios;
